/**
 * WorkflowIssueResolveModal
 *
 * Generic resolve / dismiss UI for any open ``WorkflowIssue``. Reads
 * the issue from ``GET /workflows/issues/<uuid>/`` and posts to
 * ``/resolve/`` (grant exception or mark remediated) or ``/dismiss/``
 * (withdraw - the issue no longer applies).
 *
 * The shape is resource-agnostic on purpose:
 *
 * * ``context.subject`` + ``context.meta_sections`` come from the
 *   resource adapter's ``describe_resource`` hook so the modal can lead
 *   with "Registration X for customer Y", not "ApprovalRule.fired".
 * * ``policy_snapshot`` carries the blocker / issue policy snapshot as
 *   it stood when the issue was raised, so we can render the rule name
 *   and the catalog of allowed reason codes without a second lookup.
 * * ``captured_values`` is a rule trace (predicate, observed values).
 *
 * Replaces ``ExceptionOverrideModal`` from the legacy RuleException
 * surface - the old commencement_date_override field is preserved as
 * an optional capture that flows into the engine's ``captured_values``
 * snapshot when the approver overrides the rule.
 */

import { useMemo, useState } from "react";
import {
  CalendarClock,
  Check,
  ClipboardList,
  FileWarning,
  ShieldAlert,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Label } from "@/shared/components/ui/label";
import {
  SearchableSelect,
  type SearchableSelectGroup,
  type SearchableSelectOption,
} from "@/shared/components/ui/searchable-select";
import { StatusBadge } from "@/admin/components/shared/status-badge";
import { usePermissions } from "@/admin/hooks/use-permissions";
import {
  useDismissWorkflowIssue,
  useExceptionReasons,
  useResolveWorkflowIssue,
  useWorkflowIssue,
} from "@/shared/hooks/use-workflows";
import { ACTION_REQUEST_ISSUE_KIND } from "@/shared/lib/workflows/types";
import type {
  WorkflowIssue,
  WorkflowIssueContext,
  WorkflowIssueReasonOption,
  WorkflowIssueResolvePayload,
} from "@/shared/lib/workflows/types";
import { toastApiError } from "@/shared/lib/api-error";
import { cn } from "@/shared/lib/utils";
import { useForm, type FormField } from "@/shared/lib/forms";
import { addressDisplay, isAddressLike } from "@/shared/lib/formatting";

/**
 * Preview-mode shape for callers that want to use the modal as a
 * *visual* exception flow without a real `WorkflowIssue` UUID - e.g.
 * menu actions whose backend workflow hasn't been wired yet but whose
 * UX should already match the canonical override experience.
 *
 * In preview mode the modal:
 *   • skips the GET /workflows/issues/<uuid>/ fetch,
 *   • bypasses the approver permission gate,
 *   • shows a generic "Pending" pill in place of the issue status,
 *   • hides the Deny/dismiss controls (no issue to dismiss),
 *   • and on submit fires `preview.onConfirm` with the same payload
 *     shape the resolve mutation would have sent - so the caller can
 *     toast "coming soon", call its own mutation, or both.
 */
export interface WorkflowIssuePreview {
  /** Subject + meta + trigger panel - same shape the backend sends. */
  context: WorkflowIssueContext;
  /** Catalog of allowed exceptions for the SearchableSelect. */
  reasonOptions: WorkflowIssueReasonOption[];
  /** Reason code to pre-select; falls back to the first option. */
  defaultReasonCode?: string;
  /** Pseudo-status pill label; defaults to "PENDING". */
  statusLabel?: string;
  /** Confirm CTA label; defaults to "Override exception". */
  confirmLabel?: string;
  /** Show the optional commencement-date override field. Only relevant
   *  for actions that override a time-gated rule (e.g. bypass 90-day window). */
  showCommencementDate?: boolean;
  /** Makes the note mandatory; this is its error message. */
  noteRequiredMessage?: string;
  /** Note placeholder when the note is mandatory. */
  notePlaceholder?: string;
  /** Fired on submit; the modal closes once the callback resolves. */
  onConfirm: (payload: WorkflowIssueResolvePayload) => void | Promise<void>;
}

interface WorkflowIssueResolveModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** UUID of an existing issue. Mutually exclusive with `preview`. */
  uuid: string | null;
  /** Preview-mode payload - see {@link WorkflowIssuePreview}. */
  preview?: WorkflowIssuePreview;
}

interface ReasonVisual {
  icon: LucideIcon;
  iconClassName: string;
}

function resolveReasonVisual(code: string): ReasonVisual {
  const normalized = code.toLowerCase();
  if (
    normalized.includes("date") ||
    normalized.includes("late") ||
    normalized.includes("commencement") ||
    normalized.includes("timing") ||
    normalized.includes("window")
  ) {
    return {
      icon: CalendarClock,
      iconClassName: "bg-[var(--status-warn-bg)] text-[var(--status-warn)]",
    };
  }
  if (
    normalized.includes("non_standard") ||
    normalized.includes("warranty") ||
    normalized.includes("coverage")
  ) {
    return {
      icon: ShieldAlert,
      iconClassName: "bg-[var(--green-bg)] text-[var(--green)]",
    };
  }
  if (
    normalized.includes("document") ||
    normalized.includes("evidence") ||
    normalized.includes("proof")
  ) {
    return {
      icon: ClipboardList,
      iconClassName: "bg-[var(--green-bg)] text-[var(--green)]",
    };
  }
  return {
    icon: FileWarning,
    iconClassName: "bg-[var(--bg-subtle)] text-[var(--ink-2)]",
  };
}

function humaniseSlug(slug: string): string {
  return slug.replace(/_/g, " ").replace(/\b\p{L}/gu, (c) => c.toUpperCase());
}

function reasonOptionsFromIssue(
  issue: WorkflowIssue,
): WorkflowIssueReasonOption[] {
  if (Array.isArray(issue.reason_options) && issue.reason_options.length > 0) {
    return issue.reason_options.filter(
      (entry): entry is WorkflowIssueReasonOption =>
        Boolean(
          entry && typeof entry.code === "string" && entry.code.length > 0,
        ),
    );
  }

  const snapshot = issue.policy_snapshot ?? {};
  const fromSnapshot = (snapshot.required_reason_codes ?? []) as unknown;
  if (Array.isArray(fromSnapshot) && fromSnapshot.length > 0) {
    return fromSnapshot
      .filter(
        (entry): entry is string =>
          typeof entry === "string" && entry.length > 0,
      )
      .map((code) => ({ code, label: code }));
  }
  if (issue.reason_codes.length > 0) {
    return issue.reason_codes.map((code) => ({ code, label: code }));
  }
  return [];
}

function mergeReasonOptions(
  suggested: WorkflowIssueReasonOption[],
  systemOptions: WorkflowIssueReasonOption[],
): WorkflowIssueReasonOption[] {
  const byCode = new Map<string, WorkflowIssueReasonOption>();
  for (const option of [...suggested, ...systemOptions]) {
    const code = option.code?.trim();
    if (!code) continue;
    const existing = byCode.get(code);
    if (existing) {
      byCode.set(code, {
        ...existing,
        label: existing.label || option.label,
        description: existing.description || option.description,
      });
      continue;
    }
    byCode.set(code, {
      ...option,
      code,
      label:
        option.label && option.label.trim().length > 0
          ? option.label
          : humaniseSlug(code),
    });
  }
  return [...byCode.values()];
}

function optionLabel(option: WorkflowIssueReasonOption): string {
  return option.label && option.label.trim().length > 0
    ? option.label
    : humaniseSlug(option.code);
}

function deriveContext(issue: WorkflowIssue): WorkflowIssueContext {
  // Backend always sends ``context`` now, but stay defensive in case
  // an adapter regression yields a partial payload - the modal must
  // still render a meaningful header rather than a runtime crash.
  if (issue.context) return issue.context;
  const short = issue.resource_id ? issue.resource_id.slice(0, 8) : "";
  return {
    subject: {
      kind: humaniseSlug(issue.resource_type || "Resource"),
      title: issue.summary || "Workflow issue",
      subtitle: short,
    },
    meta_sections: [],
    trigger: {
      title:
        ((issue.policy_snapshot ?? {}).rule_name as string | undefined) ||
        issue.summary ||
        "Manual exception",
      description: issue.detail || "",
      breakdown: [],
    },
  };
}

function formatContextText(value: unknown): string {
  if (isAddressLike(value)) return addressDisplay(value) || "-";
  if (typeof value === "string") return addressDisplay(value) || value;
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
}

export function ExceptionModal({
  open,
  onOpenChange,
  uuid,
  preview,
}: WorkflowIssueResolveModalProps) {
  const { t } = useTranslation();
  const { can } = usePermissions();
  // Preview mode shouldn't trigger the network fetch - keep the hook
  // call unconditional (rules of hooks) but pass `null` so the query
  // is disabled at the source.
  const { data: fetchedIssue, isLoading: isFetching } = useWorkflowIssue(
    preview ? null : uuid,
  );
  const resolveMutation = useResolveWorkflowIssue();
  const dismissMutation = useDismissWorkflowIssue();
  // Only fetch reasons when the modal is actually open and not in
  // preview mode (preview supplies its own reasonOptions). Keeps the
  // exceptions endpoint from firing while the modal is closed.
  const { data: exceptionReasons = [] } = useExceptionReasons(open && !preview);
  const [showDismissForm, setShowDismissForm] = useState(false);

  const issue = preview ? null : fetchedIssue;
  // A request to perform one action (WPS-180): approving it performs the
  // action, so a commencement override has nothing to apply to.
  const isActionRequest = issue?.issue_kind === ACTION_REQUEST_ISSUE_KIND;
  const context = preview?.context ?? (issue ? deriveContext(issue) : null);
  const isLoading = preview ? false : isFetching;
  const suggestedReasonOptions = useMemo<WorkflowIssueReasonOption[]>(
    () =>
      preview
        ? preview.defaultReasonCode
          ? preview.reasonOptions.filter(
              (option) => option.code === preview.defaultReasonCode,
            )
          : []
        : issue
          ? reasonOptionsFromIssue(issue)
          : [],
    [preview, issue],
  );
  const reasonCatalog = useMemo<WorkflowIssueReasonOption[]>(() => {
    // In preview mode the reasons fetch is disabled, so the caller-supplied
    // ``preview.reasonOptions`` IS the catalog (the full allowed set). Outside
    // preview we build the catalog from the fetched active exception reasons.
    // Either way, ``suggestedReasonOptions`` is surfaced as the "Suggested"
    // group and the rest fall under "All exceptions".
    const configuredReasonOptions = preview
      ? (preview.reasonOptions ?? [])
      : exceptionReasons
          .filter((reason) => reason.is_active)
          .map((reason) => ({
            code: reason.key,
            label: reason.title,
            description: reason.description,
          }));
    return mergeReasonOptions(suggestedReasonOptions, configuredReasonOptions);
  }, [exceptionReasons, suggestedReasonOptions, preview]);
  const suggestedReasonCodes = useMemo(
    () => new Set(suggestedReasonOptions.map((option) => option.code)),
    [suggestedReasonOptions],
  );

  const reasonOptionsByCode = useMemo(() => {
    const byCode = new Map<string, WorkflowIssueReasonOption>();
    for (const option of reasonCatalog) {
      byCode.set(option.code, option);
    }
    return byCode;
  }, [reasonCatalog]);

  const reasonGroups = useMemo<SearchableSelectGroup[]>(() => {
    if (reasonCatalog.length === 0) return [];
    const toOption = (
      option: WorkflowIssueReasonOption,
    ): SearchableSelectOption => ({
      value: option.code,
      label: optionLabel(option),
      searchText: `${option.code} ${option.label}`,
      keywords: [option.code, option.label],
    });
    const suggested = reasonCatalog
      .filter((option) => suggestedReasonCodes.has(option.code))
      .map(toOption);
    const allOther = reasonCatalog
      .filter((option) => !suggestedReasonCodes.has(option.code))
      .map(toOption);

    return [
      ...(suggested.length > 0
        ? [{ label: "Suggested for this issue", options: suggested }]
        : []),
      { label: "All exceptions", options: allOther },
    ].filter((group) => group.options.length > 0);
  }, [reasonCatalog, suggestedReasonCodes]);

  const defaultReasonCodes = useMemo(() => {
    const defaultCode =
      preview?.defaultReasonCode || suggestedReasonOptions[0]?.code || "";
    return defaultCode ? [defaultCode] : [];
  }, [preview?.defaultReasonCode, suggestedReasonOptions]);

  // ──────────────────────────────────────────────────────────────────
  // Resolve form - searchable multi-select exception selector, free-text
  // notes for the audit log, optional commencement-date override that
  // piggy-backs on captured_values.
  // Kept deliberately tight: this is a routine daily task for Amanda's
  // team, not an alarm bell. No rule-trace debug block, no jargon.
  // ──────────────────────────────────────────────────────────────────
  const resolveFields = useMemo<FormField[]>(
    () => [
      {
        type: "custom",
        name: "reason_codes",
        value: defaultReasonCodes,
        required: "Select at least one exception",
        render: (formContext) => {
          const selected =
            (formContext.values.reason_codes as string[] | undefined) ?? [];
          const touched = formContext.touched?.reason_codes;
          const errorMessage = formContext.errors.reason_codes as
            | string
            | undefined;
          const error = touched ? errorMessage : undefined;
          const selectedOptions = selected
            .map((code) => reasonOptionsByCode.get(code))
            .filter((option): option is WorkflowIssueReasonOption =>
              Boolean(option),
            );
          const selectedLabels = selectedOptions.map(optionLabel);
          const firstSelected = selected[0] ?? "";
          const toggleReason = (code: string) => {
            const next = selected.includes(code)
              ? selected.filter((current) => current !== code)
              : [...selected, code];
            formContext.setFieldValue("reason_codes", next);
          };

          return (
            <div className="flex flex-col gap-1.5">
              <div className="flex items-baseline justify-between gap-2">
                <Label className="clara-label">Exception reasons</Label>
                <span className="text-[11px] text-[var(--ink-3)]">
                  Select one or more
                </span>
              </div>
              <SearchableSelect
                value={firstSelected}
                onValueChange={toggleReason}
                groups={reasonGroups}
                closeOnSelect={false}
                isOptionSelected={(option) => selected.includes(option.value)}
                placeholder="Select exceptions"
                searchPlaceholder="Search by name or code..."
                emptyMessage="No exceptions in the catalog"
                triggerClassName={cn(
                  "h-auto min-h-[60px] rounded-[8px] border-[0.5px] bg-[var(--bg-light)] px-3 py-2.5 transition-colors hover:border-[var(--ink-3)]",
                  error
                    ? "border-[var(--status-error)]"
                    : "border-[var(--rule-strong)]",
                )}
                contentClassName="max-w-[480px]"
                renderValue={(option) => {
                  if (selectedLabels.length === 0 || !option) {
                    return (
                      <span className="text-[13px] text-[var(--ink-3)]">
                        Select exceptions
                      </span>
                    );
                  }
                  return (
                    <SelectedReasonSummary selectedOptions={selectedOptions} />
                  );
                }}
                renderOption={(option, state) => {
                  const reason = reasonOptionsByCode.get(option.value);
                  return (
                    <ReasonRow
                      code={option.value}
                      label={option.label}
                      description={reason?.description}
                      selected={state.selected}
                      size="sm"
                    />
                  );
                }}
              />
              {selectedLabels.length > 1 ? (
                <div className="mt-1 flex flex-wrap gap-1">
                  {selectedLabels.map((label) => (
                    <span
                      key={label}
                      className="rounded-full border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)] px-2 py-0.5 text-[11px] leading-4 text-[var(--green)]"
                    >
                      {label}
                    </span>
                  ))}
                </div>
              ) : null}
              {error ? (
                <p className="text-[11px] text-[var(--status-error)]">
                  {error}
                </p>
              ) : null}
            </div>
          );
        },
      },
      {
        type: "textarea",
        name: "resolution_text",
        label: "Notes",
        placeholder: preview?.noteRequiredMessage
          ? (preview.notePlaceholder ?? "")
          : "Optional context for the audit log - what you want your future self to remember.",
        value: "",
        // Optional by default: the initial override justification is captured
        // upstream (e.g. the resolution workspace's required justification
        // field), so the popup must not demand a duplicate comment (WPS-117).
        // A caller with no upstream note makes it mandatory instead.
        ...(preview?.noteRequiredMessage
          ? { required: preview.noteRequiredMessage }
          : {}),
        maxLength: 4000,
      },
      // Only show commencement-date override for time-gated exceptions
      // (e.g. bypass 90-day window). Non-preview (real workflow issues)
      // always include it; preview mode requires explicit opt-in.
      ...((!preview && !isActionRequest) || preview?.showCommencementDate
        ? [
            {
              type: "datepicker" as const,
              name: "commencement_date_override",
              label: "Override commencement date (optional)",
              value: "",
            },
          ]
        : []),
    ],
    [
      defaultReasonCodes,
      reasonGroups,
      reasonOptionsByCode,
      preview,
      isActionRequest,
    ],
  );

  const { Form: ResolveForm } = useForm(
    {
      name: "workflow-issue-resolve-form",
      submit: "footer",
      fields: resolveFields,
    },
    async (rawValues) => {
      const values = rawValues as {
        reason_codes: string[];
        resolution_text: string;
        commencement_date_override: string;
      };
      const reasonCodes = Array.isArray(values.reason_codes)
        ? values.reason_codes.filter(Boolean)
        : [];
      if (reasonCodes.length === 0) {
        toast.error("Select at least one exception");
        return;
      }
      if (preview?.noteRequiredMessage && !values.resolution_text.trim()) {
        toast.error(preview.noteRequiredMessage);
        return;
      }
      const payload: WorkflowIssueResolvePayload = {
        // The Override button always grants an exception; the
        // "remediated" semantic belongs on a different control if and
        // when it ever needs to be exposed in this surface.
        resolution_kind: "exception_granted",
        resolution_text: values.resolution_text.trim(),
        reason_codes: reasonCodes,
        captured_values: values.commencement_date_override
          ? { commencement_date_override: values.commencement_date_override }
          : {},
      };

      // Preview mode short-circuits the backend mutation - the caller
      // owns success-side effects (typically a "coming soon" toast or
      // a stub mutation while the workflow ships).
      if (preview) {
        try {
          await preview.onConfirm(payload);
          onOpenChange(false);
        } catch (err) {
          toastApiError(err, "Failed to file exception");
        }
        return;
      }

      if (!uuid) return;
      try {
        await resolveMutation.mutateAsync({ uuid, payload });
        toast.success(
          isActionRequest
            ? t("workflowIssues.requestApproved")
            : t("workflowIssues.exceptionOverridden"),
        );
        onOpenChange(false);
      } catch (err) {
        toastApiError(err, "Failed to override exception");
      }
    },
    [uuid, preview, onOpenChange, resolveMutation, isActionRequest, t],
  );

  const dismissFields = useMemo<FormField[]>(
    () => [
      {
        type: "textarea",
        name: "resolution_text",
        label: "Denial reason",
        placeholder:
          "e.g. Insufficient documentation provided to support late registration.",
        hint: "The submitter and the audit trail will see this reason.",
        value: "",
        required: "Denial reason is required",
        maxLength: 2000,
      },
    ],
    [],
  );

  const { Form: DismissForm } = useForm(
    {
      name: "workflow-issue-dismiss-form",
      submit: "footer",
      fields: dismissFields,
    },
    async (rawValues) => {
      if (!uuid) return;
      const values = rawValues as { resolution_text: string };
      try {
        await dismissMutation.mutateAsync({
          uuid,
          payload: { resolution_text: values.resolution_text.trim() },
        });
        toast.success("Exception denied");
        setShowDismissForm(false);
        onOpenChange(false);
      } catch (err) {
        toastApiError(err, "Failed to deny exception");
      }
    },
    [uuid],
  );

  // Permission gate matches the backend ``_can_resolve_issue`` shape:
  // either the generic ``exceptions.approve`` codename, the legacy
  // ``registrations.exceptions.approve`` codename, or the dynamically
  // stored ``approver_role`` slug copied from the policy snapshot.
  // Preview mode bypasses the gate entirely - the caller is responsible
  // for gating the menu action upstream.
  const approverRole =
    (issue?.policy_snapshot?.approver_role as string | undefined) ?? "";
  const canApprove = preview
    ? true
    : can("exceptions.approve") ||
      can("registrations.exceptions.approve") ||
      (approverRole ? can(approverRole) : false);
  const canDeny = preview
    ? false
    : can("exceptions.approve") ||
      can("registrations.exceptions.deny") ||
      canApprove;
  const isOpen = preview ? true : issue?.status === "OPEN";
  const showResolveForm = isOpen && canApprove && !showDismissForm;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) setShowDismissForm(false);
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent
        bare
        className="max-w-[560px] sm:max-w-[560px] max-h-[min(88vh,860px)]"
      >
        {/* Hidden a11y title/description so screen-reader users still
            hear a meaningful modal name; the visible hierarchy uses
            the subject from the adapter context. */}
        <DialogHeader className="sr-only">
          <DialogTitle>
            {context?.subject.title || "Workflow exception"}
          </DialogTitle>
          <DialogDescription>
            {context?.trigger.title || "Workflow issue detail"}
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col">
          {isLoading || !context || (!preview && !issue) ? (
            <div className="px-6 py-10">
              <LoadingState />
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
              <section className="border-b-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)]/60 px-6 pt-5 pb-5">
                <div className="flex items-start justify-between gap-3">
                  <p className="clara-label text-[var(--ink-3)]">
                    Action requiring exception
                  </p>
                  {preview ? (
                    <span className="inline-flex items-center rounded-[3px] bg-[var(--status-warn-bg)] px-1.5 py-px text-[10px] font-medium uppercase tracking-[0.1em] text-[var(--status-warn)]">
                      {preview.statusLabel ?? "Pending"}
                    </span>
                  ) : issue ? (
                    <StatusBadge status={issue.status} />
                  ) : null}
                </div>
                <h2 className="mt-2 line-clamp-2 font-display text-[24px] leading-[1.1] tracking-[-0.005em] text-[var(--ink)]">
                  {formatContextText(context.trigger.title)}
                </h2>
                {context.trigger.description ? (
                  <p className="mt-1 line-clamp-3 text-[12px] leading-[1.5] text-[var(--ink-3)]">
                    {formatContextText(context.trigger.description)}
                  </p>
                ) : null}
                {typeof issue?.captured_values?.transfer_uuid === "string" ? (
                  <a
                    href={`/transfers/${issue.captured_values.transfer_uuid}`}
                    className="mt-2 inline-block text-[12px] text-[var(--ink)] underline decoration-[var(--ink-3)] underline-offset-[3px] hover:decoration-[var(--ink)]"
                  >
                    {t("workflowIssues.openTransfer", "Open transfer")}
                  </a>
                ) : null}
              </section>

              {/* ─── 2. ACTION FORM - approver actions ──────────────── */}
              {showResolveForm || showDismissForm ? (
                <section className="bg-[var(--bg-subtle)]/60 px-6 py-5">
                  {showResolveForm ? (
                    <ResolveForm className="flex flex-col gap-3" />
                  ) : null}
                  {showDismissForm ? (
                    <DismissForm className="flex flex-col gap-2.5" />
                  ) : null}
                </section>
              ) : null}
            </div>
          )}

          <DialogFooter className="border-t-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-5 py-3 sm:items-center sm:justify-between">
            {isOpen && !showDismissForm && canApprove ? (
              <div className="flex w-full flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex">
                  {canDeny ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="xs"
                      onClick={() => setShowDismissForm(true)}
                      className="px-0 text-[11px] uppercase tracking-[0.1em] text-[var(--status-error)] hover:bg-transparent hover:text-[var(--status-error)] hover:opacity-80"
                    >
                      Deny request
                    </Button>
                  ) : null}
                </div>
                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                    className="min-h-[36px] rounded-[4px] border-[0.5px] border-[var(--rule-strong)] px-4 text-[11px] uppercase tracking-[0.1em] text-[var(--ink)]"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    form="workflow-issue-resolve-form"
                    disabled={resolveMutation.isPending}
                    className="group min-h-[36px] gap-1.5 rounded-[4px] bg-[var(--green)] px-5 text-[11px] uppercase tracking-[0.1em] text-white transition-all hover:bg-[var(--green-hover)] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {resolveMutation.isPending ? (
                      <>
                        <span className="inline-block h-3 w-3 animate-spin rounded-full border border-white/30 border-t-white" />
                        Overriding…
                      </>
                    ) : (
                      <>
                        <Check className="h-3.5 w-3.5 transition-transform group-hover:scale-110" />
                        {preview?.confirmLabel ??
                          (isActionRequest
                            ? t("workflowIssues.approveRequest")
                            : t("entityAction.overrideException"))}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ) : showDismissForm ? (
              <div className="flex w-full items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  onClick={() => setShowDismissForm(false)}
                  className="px-2 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  form="workflow-issue-dismiss-form"
                  disabled={dismissMutation.isPending}
                  className="min-h-[36px] gap-1.5 rounded-[4px] bg-[var(--status-error)] px-4 text-[11px] uppercase tracking-[0.1em] text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                >
                  {dismissMutation.isPending ? (
                    <>
                      <span className="inline-block h-3 w-3 animate-spin rounded-full border border-white/30 border-t-white" />
                      Denying…
                    </>
                  ) : (
                    "Confirm deny"
                  )}
                </Button>
              </div>
            ) : (
              <div className="flex w-full justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  className="min-h-[36px] rounded-[4px] border-[0.5px] border-[var(--rule-strong)] px-4 text-[11px] uppercase tracking-[0.1em] text-[var(--ink)]"
                >
                  Close
                </Button>
              </div>
            )}
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export const WorkflowIssueResolveModal = ExceptionModal;

function ReasonRow({
  code,
  label,
  description,
  selected = false,
  size = "md",
}: {
  code: string;
  label?: string;
  description?: string;
  selected?: boolean;
  size?: "sm" | "md";
}) {
  const { icon: Icon, iconClassName } = resolveReasonVisual(code);
  const iconBox = size === "sm" ? "h-7 w-7" : "h-9 w-9";
  const iconSize = size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4";

  return (
    <div className="flex min-w-0 items-start gap-3">
      <span
        className={cn(
          "mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-[3px] border-[0.5px] transition-colors",
          selected
            ? "border-[var(--green)] bg-[var(--green)] text-white"
            : "border-[var(--rule-strong)] bg-[var(--bg)] text-transparent",
        )}
      >
        <Check className="h-3 w-3" />
      </span>
      <div
        className={cn(
          "flex shrink-0 items-center justify-center rounded-[8px]",
          iconBox,
          iconClassName,
        )}
      >
        <Icon className={iconSize} />
      </div>
      <div className="min-w-0 flex-1 pt-0.5">
        <div className="truncate text-[13px] leading-5 text-[var(--ink)]">
          {label && label.trim().length > 0 ? label : humaniseSlug(code)}
        </div>
        {description ? (
          <div className="mt-0.5 line-clamp-2 text-[11px] leading-[1.45] text-[var(--ink-3)]">
            {description}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function SelectedReasonSummary({
  selectedOptions,
}: {
  selectedOptions: WorkflowIssueReasonOption[];
}) {
  const firstOption = selectedOptions[0];
  if (!firstOption) return null;

  if (selectedOptions.length === 1) {
    return (
      <span className="block min-w-0">
        <span className="block truncate text-[13px] text-[var(--ink)]">
          {optionLabel(firstOption)}
        </span>
        {firstOption.description ? (
          <span className="mt-0.5 block truncate text-[11px] leading-[1.35] text-[var(--ink-3)]">
            {firstOption.description}
          </span>
        ) : null}
      </span>
    );
  }

  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <span className="truncate text-[13px] text-[var(--ink)]">
        {optionLabel(firstOption)}
      </span>
      <span className="shrink-0 rounded-full bg-[var(--green-bg)] px-1.5 py-px text-[11px] leading-4 text-[var(--green)]">
        +{selectedOptions.length - 1}
      </span>
    </span>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-col gap-3 py-6">
      <div className="h-4 w-32 animate-pulse rounded-[4px] bg-[var(--skeleton-base)]" />
      <div className="h-20 w-full animate-pulse rounded-[8px] bg-[var(--skeleton-base)]" />
      <div className="h-4 w-24 animate-pulse rounded-[4px] bg-[var(--skeleton-base)]" />
      <div className="h-12 w-full animate-pulse rounded-[8px] bg-[var(--skeleton-base)]" />
    </div>
  );
}
